// ─────────────────────────────────────────────────────────────────────────────
// client/src/hooks/useTheme.ts
//
// Fetches the account's white-label theme on login and applies CSS custom
// properties dynamically. Company name is displayed in sidebar header and
// report headers.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { useThemeStore, type AccountTheme } from '../stores/themeStore';

// ─── Query Hook — Fetch & apply theme ───────────────────────────────────────

export function useAccountTheme() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const { setTheme, resetTheme, theme, isLoaded } = useThemeStore();

  const accountId = user?.accountId;

  const query = useQuery<{ data: AccountTheme }>({
    queryKey: ['account-theme', accountId],
    queryFn: async () => {
      if (!accountId) {
        return {
          data: {
            accountId: 0,
            accentColor: '#1877f2',
            companyName: 'ENERLYZE',
          },
        };
      }
      const { data } = await api.get(`/accounts/${accountId}/theme`);
      return data;
    },
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  useEffect(() => {
    if (query.data?.data) {
      setTheme(query.data.data);
    }
  }, [query.data, setTheme]);

  useEffect(() => {
    if (!accessToken) {
      resetTheme();
    }
  }, [accessToken, resetTheme]);

  return {
    theme: query.data?.data ?? theme,
    isLoading: query.isLoading,
    isLoaded,
    refetch: query.refetch,
  };
}

// ─── Mutation Hooks — Update theme ──────────────────────────────────────────

export function useUpdateTheme(accountId: number) {
  const queryClient = useQueryClient();
  const { setTheme } = useThemeStore();

  return useMutation({
    mutationFn: async (payload: { accentColor?: string; companyName?: string }) => {
      const { data } = await api.put(`/accounts/${accountId}/theme`, payload);
      return data.data as AccountTheme;
    },
    onSuccess: (updatedTheme) => {
      setTheme(updatedTheme);
      queryClient.invalidateQueries({ queryKey: ['account-theme', accountId] });
    },
  });
}
